import { defineStore } from "pinia";
import { openDB } from "idb";

const DB_NAME = "issueNoteDraftsDB";
const DB_VERSION = 1;
const STORE_NAME = "drafts";

let dbPromise = null;

function getDB() {
  if (!dbPromise) {
    dbPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME, { keyPath: "key" });
        }
      },
    });
  }
  return dbPromise;
}

export const useIssueNoteDraftsStore = defineStore("issueNoteDrafts", {
  state: () => ({
    draftKeys: new Set(),
    isInitialized: false,
  }),
  actions: {
    async initFromIndexedDB() {
      if (this.isInitialized) return;

      try {
        const db = await getDB();
        const allKeys = await db.getAllKeys(STORE_NAME);
        this.draftKeys = new Set(allKeys);
        this.isInitialized = true;
      } catch (error) {
        console.error("Failed to load draft keys from IndexedDB:", error);
        this.isInitialized = true;
      }
    },
    getKey(userId, type, objectId = null) {
      return `${userId}_${type}_${objectId}`;
    },
    hasDraft(userId, type, objectId = null) {
      if (!userId) return false;
      return this.draftKeys.has(this.getKey(userId, type, objectId));
    },
    async saveDraft(userId, type, objectId, data) {
      if (!userId) return;

      const key = this.getKey(userId, type, objectId);
      const draftData = {
        ...data,
        updatedAt: new Date().toISOString(),
      };

      try {
        const db = await getDB();
        await db.put(STORE_NAME, { key, data: draftData });
        this.draftKeys = new Set([...this.draftKeys, key]);
      } catch (error) {
        console.error("Failed to save draft:", error);
      }
    },
    async getDraft(userId, type, objectId = null) {
      if (!userId) return null;

      const key = this.getKey(userId, type, objectId);
      if (!this.draftKeys.has(key)) return null;

      try {
        const db = await getDB();
        const result = await db.get(STORE_NAME, key);
        return result?.data || null;
      } catch (error) {
        console.error("Failed to get draft:", error);
        return null;
      }
    },
    async deleteDraft(userId, type, objectId = null) {
      if (!userId) return;

      const key = this.getKey(userId, type, objectId);
      if (!this.draftKeys.has(key)) return;

      try {
        const db = await getDB();
        await db.delete(STORE_NAME, key);
        const newKeys = new Set(this.draftKeys);
        newKeys.delete(key);
        this.draftKeys = newKeys;
      } catch (error) {
        console.error("Failed to delete draft:", error);
      }
    },
  },
});
